import { useContext, useEffect, useState } from 'react';
import { LoadingState } from 'tendril-v1/src/types/LoadingStates.type';
import { UserProviderContext } from 'tendril-v1/src/components/app/UserProvider';
import { featuresStore } from 'tendril-v1/src/components/app/FeaturesProvider';
import recycleContacts from 'tendril-v2/src/Data/DataSource/contacts-nest/callSession/recycleContacts';
import {
  type ContactListByCallSession,
  type ContactListByCallSessionView,
} from 'tendril-v2/src/Data/DataSource/contacts-nest/callSession/entities/callSession.entity';

const { IDLE, LOADING, ERROR, SUCCESS } = LoadingState;

export interface useCallSessionViewModelI {
  callSessions: ContactListByCallSession[];
  titlesSession: ContactListByCallSessionView[];
  loadingState: useCallSessionViewModelLoadingState;
  formatAndSetData: (callSessions: ContactListByCallSession[]) => void;
  fetchCallSessionsBySdrId: (sdrId: string, orgId: string) => Promise<void>;
}

interface useCallSessionViewModelLoadingState {
  fetchCallSessionsBySdrId: LoadingState;
}

const useCallSessionViewModel = (): useCallSessionViewModelI => {
  const [callSessions, setCallSessions] = useState<ContactListByCallSession[]>([]);
  const [titlesSession, setTitles] = useState<ContactListByCallSessionView[]>([]);
  const { currentUser } = useContext(UserProviderContext);
  const [loadingState, setLoadingState] = useState<useCallSessionViewModelLoadingState>({
    fetchCallSessionsBySdrId: IDLE,
  });
  const { contactsFilter } = useContext(featuresStore).featuresState;

  const formatAndSetData = (callSessions: ContactListByCallSession[]): void => {
    const formattedData: ContactListByCallSessionView[] = callSessions.map((callSession, index) => {
      const { callSessionId, listTitle } = callSession;

      const list = listTitle ?? 'Name unavailable';
      const shownTitle = `Session:${list}-${callSessionId}`;

      return {
        id: callSessionId,
        title: shownTitle,
        type: `filteredContacts`,
      };
    });

    // This makes lists appear in descendant order (virtually taking the createdAt attribute: by default db sorts by createdAt asc).
    const sortedData = formattedData.length > 0 ? formattedData.reverse() : formattedData;

    setTitles(sortedData);
  };

  const fetchCallSessionsBySdrId = async (sdrId: string, orgId: string): Promise<void> => {
    setLoadingState((prevState) => ({
      ...prevState,
      fetchCallSessionsBySdrId: LOADING,
    }));

    const { data, error, success } = await recycleContacts(sdrId, orgId);

    if (data === undefined || error !== undefined || success === false) {
      setLoadingState({ ...loadingState, fetchCallSessionsBySdrId: ERROR });
      return;
    }

    formatAndSetData(data);

    setCallSessions(data);

    setLoadingState((prevState) => ({
      ...prevState,
      fetchCallSessionsBySdrId: SUCCESS,
    }));
  };

  useEffect(() => {
    if (loadingState.fetchCallSessionsBySdrId !== LOADING || contactsFilter) {
      void fetchCallSessionsBySdrId(currentUser.userId, currentUser.organizationId).finally();
    }
  }, []);

  return { callSessions, titlesSession, loadingState, formatAndSetData, fetchCallSessionsBySdrId };
};

export default useCallSessionViewModel;
